// Generic modal/backdrop show-hide helpers, covering the two patterns used
// across the app: a plain instant toggle (restart/victory/stats modals), and
// an animated fade+scale toggle (the keypad modal).
//
// Every show call also arms a same-hash history entry so the browser back
// button closes the modal instead of navigating away, and every hide call
// disarms it again - whether the hide came from a normal close (X, Cancel,
// Escape, backdrop click) or from the back-press itself. That keeps the
// history stack clean: a modal that's opened and closed a dozen times in a
// row (the keypad, easily, over one game) never leaves a dozen dead entries
// behind for a later back-press to work through with no visible effect.
//
// Only one modal is ever open at a time in this app, so a single slot (not
// a stack) is enough here.

let armedHide = null;
let suppressNextPopstate = false;
let pendingDisarmResolve = null;

window.addEventListener('popstate', (event) => {
    if (suppressNextPopstate) {
        // Our own disarm() below causes this - not a real back-press, and
        // not for anything else (e.g. app.js's "leave game?" guard) to react
        // to either. Resolved here (rather than via a second listener,
        // which stopImmediatePropagation would prevent from ever firing).
        suppressNextPopstate = false;
        event.stopImmediatePropagation();
        if (pendingDisarmResolve) {
            const resolve = pendingDisarmResolve;
            pendingDisarmResolve = null;
            resolve();
        }
        return;
    }
    if (armedHide) {
        const hide = armedHide;
        armedHide = null; // cleared first so hide()'s own disarm() call below is a no-op
        event.stopImmediatePropagation(); // a modal owns this back-press, not e.g. app.js's game guard
        hide();
    }
});

function armModalGuard(hide) {
    // Same URL, a new history entry - see app.js's pushHistoryGuard for why
    // this needs the query string too, not just the path.
    history.pushState({ modal: true }, '', location.pathname + location.search);
    armedHide = hide;
}

// Returns a promise resolving once the disarm has actually taken effect
// (immediately if there was nothing to disarm). `history.back()` is
// asynchronous - if a caller fires ANOTHER history-touching navigation
// (e.g. going home) in the same synchronous tick right after hiding a
// modal, the two operations race and can corrupt each other. Callers that
// chain a real navigation after a hide must await this instead of firing
// both at once.
function disarmModalGuard() {
    if (!armedHide) return Promise.resolve();
    armedHide = null;
    suppressNextPopstate = true;
    return new Promise((resolve) => {
        pendingDisarmResolve = resolve;
        history.back();
    });
}

export function showPlainModal(modalId, backdropId) {
    document.getElementById(modalId).style.display = 'block';
    document.getElementById(backdropId).style.display = 'block';
    document.body.style.overflow = 'hidden';
    armModalGuard(() => hidePlainModal(modalId, backdropId));
}

export function hidePlainModal(modalId, backdropId) {
    document.getElementById(modalId).style.display = 'none';
    document.getElementById(backdropId).style.display = 'none';
    document.body.style.overflow = '';
    return disarmModalGuard();
}

export function isModalOpen(modalId) {
    return document.getElementById(modalId).style.display === 'block';
}

export function showAnimatedModal(modalId, backdropId) {
    const modal = document.getElementById(modalId);
    const backdrop = document.getElementById(backdropId);
    modal.style.display = 'block';
    backdrop.style.display = 'block';
    document.body.style.overflow = 'hidden';
    // Small delay to allow display change before the opacity/transform transition
    setTimeout(() => {
        modal.classList.add('active');
        backdrop.classList.add('active');
    }, 10);
    armModalGuard(() => hideAnimatedModal(modalId, backdropId));
}

export function hideAnimatedModal(modalId, backdropId, transitionMs = 160) {
    const modal = document.getElementById(modalId);
    const backdrop = document.getElementById(backdropId);
    modal.classList.remove('active');
    backdrop.classList.remove('active');
    document.body.style.overflow = '';
    // Wait for the transition to complete before hiding
    setTimeout(() => {
        modal.style.display = 'none';
        backdrop.style.display = 'none';
    }, transitionMs);
    return disarmModalGuard();
}
